import { type ReactNode, Suspense } from 'react';

import { nowPlaying } from '@audacy-clients/core/atoms/player';
import { ErrorBoundary } from 'react-error-boundary';
import { useRecoilValue } from 'recoil';

import Animate from '~/components/Animate';
import BackButton from '~/components/BackButton';
import FullScreenError from '~/components/Error/FullScreenError';
import Footer from '~/components/Footer';
import footerSections from '~/components/Footer/constants';
import LayoutConstants from '~/constants/layout';
import useNavigation from '~/hooks/use-navigation';
import type { TStyles } from '~/types/emotion-styles';
import { canGoBack } from '~/utils/browser';

import styles from './styles';

export interface ILayoutProps {
  bodyCss?: TStyles;
  children: ReactNode;
  mainCss?: TStyles;
  shouldRemoveBottomPadding?: boolean;
  shouldRemoveTopPadding?: boolean;
  isBackButtonInLayoutHeader?: boolean;
}

const Layout = ({
  bodyCss,
  children,
  mainCss,
  shouldRemoveBottomPadding,
  shouldRemoveTopPadding,
  isBackButtonInLayoutHeader = true,
}: ILayoutProps): JSX.Element => {
  const { isMenuOpen } = useNavigation();
  const hasPlayer = useRecoilValue(nowPlaying);

  return (
    <div css={styles.container}>
      {/* Anything behind or below the navigation needs aria-hidden to avoid being read on iOS */}
      <div css={[styles.body, bodyCss]} aria-hidden={isMenuOpen}>
        <ErrorBoundary FallbackComponent={({ error }) => <FullScreenError error={error} />}>
          {/* do not render loading animation as this is handled in <Page>*/}
          <Suspense fallback={null}>
            {/* fade in static content like /registration, /my-audio, etc. */}
            <Animate.FadeInOnMount>
              <main
                id={LayoutConstants.mainContentId}
                css={[
                  styles.main,
                  !shouldRemoveBottomPadding && styles.mainPadding,
                  canGoBack() && styles.backButtonPadding,
                  shouldRemoveTopPadding && styles.resetTopPadding,
                  mainCss,
                ]}
              >
                {isBackButtonInLayoutHeader && <BackButton containerCss={styles.back} />}
                {children}
              </main>

              <footer css={hasPlayer && styles.playerVisible}>
                <Footer sections={footerSections} />
              </footer>
            </Animate.FadeInOnMount>
          </Suspense>
        </ErrorBoundary>
      </div>
    </div>
  );
};

export default Layout;
